// convex/lib/config/i18n_config.ts
// Configuración base de internacionalización para el sistema

/**
 * Configuración de formato de números por locale
 */
export interface NumberFormatConfig {
  /** Separador decimal */
  decimal: string;
  /** Separador de miles */
  thousands: string;
  /** Símbolo de moneda por defecto */
  currencySymbol: string;
  /** Posición del símbolo de moneda */
  currencyPosition: "before" | "after";
}

/**
 * Configuración de formato de fechas por locale
 */
export interface DateFormatConfig {
  /** Formato corto (ej: "DD/MM/YYYY") */
  short: string;
  /** Formato largo (ej: "DD de MMMM de YYYY") */
  long: string;
  /** Formato con hora (ej: "DD/MM/YYYY HH:mm") */
  withTime: string;
  /** Formato solo hora (ej: "HH:mm") */
  time: string;
  /** Primer día de la semana (0=domingo, 1=lunes) */
  firstDayOfWeek: 0 | 1 | 6;
}

/**
 * Configuración completa de un locale soportado
 */
export interface LocaleConfig {
  /** Código ISO del idioma (es, en, pt, etc.) */
  code: string;
  /** Nombre en inglés */
  name: string;
  /** Nombre en el idioma nativo */
  nativeName: string;
  /** Dirección del texto */
  direction: "ltr" | "rtl";
  /** Emoji de bandera o código de país */
  flag: string;
  /** Configuración de formatos de fecha */
  dateFormat: DateFormatConfig;
  /** Configuración de formatos de número */
  numberFormat: NumberFormatConfig;
  /** Moneda por defecto para este locale */
  defaultCurrency: string;
  /** Zona horaria por defecto */
  defaultTimezone: string;
}

/**
 * Locales soportados por el sistema
 */
export const SUPPORTED_LOCALES: readonly LocaleConfig[] = [
  {
    code: "es",
    name: "Spanish",
    nativeName: "Español",
    direction: "ltr",
    flag: "🇭🇳",
    dateFormat: {
      short: "DD/MM/YYYY",
      long: "DD [de] MMMM [de] YYYY",
      withTime: "DD/MM/YYYY HH:mm",
      time: "HH:mm",
      firstDayOfWeek: 1, // Lunes
    },
    numberFormat: {
      decimal: ".",
      thousands: ",",
      currencySymbol: "L",
      currencyPosition: "before",
    },
    defaultCurrency: "HNL",
    defaultTimezone: "America/Tegucigalpa",
  },
  {
    code: "en",
    name: "English",
    nativeName: "English",
    direction: "ltr",
    flag: "🇺🇸",
    dateFormat: {
      short: "MM/DD/YYYY",
      long: "MMMM DD, YYYY",
      withTime: "MM/DD/YYYY h:mm A",
      time: "h:mm A",
      firstDayOfWeek: 0, // Domingo
    },
    numberFormat: {
      decimal: ".",
      thousands: ",",
      currencySymbol: "$",
      currencyPosition: "before",
    },
    defaultCurrency: "USD",
    defaultTimezone: "America/New_York",
  },
] as const;

/**
 * Locale por defecto del sistema
 */
export const DEFAULT_LOCALE = "es" as const;

/**
 * Locale de fallback cuando no existe una traducción
 */
export const FALLBACK_LOCALE = "en" as const;

/**
 * Códigos de locale soportados (para validación)
 */
export type SupportedLocaleCode = (typeof SUPPORTED_LOCALES)[number]["code"];

/**
 * Lista de códigos de locale soportados
 */
export const LOCALE_CODES = SUPPORTED_LOCALES.map(
  (l) => l.code,
) as readonly SupportedLocaleCode[];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Obtiene la configuración de un locale por su código
 */
export function getLocaleConfig(code: string): LocaleConfig | undefined {
  return SUPPORTED_LOCALES.find((l) => l.code === code);
}

/**
 * Verifica si un código de locale está soportado
 */
export function isSupportedLocale(code: string): code is SupportedLocaleCode {
  return LOCALE_CODES.includes(code as SupportedLocaleCode);
}

/**
 * Obtiene el locale por defecto o el especificado si es válido
 */
export function getValidLocale(code?: string | null): SupportedLocaleCode {
  if (code && isSupportedLocale(code)) {
    return code;
  }
  return DEFAULT_LOCALE;
}

/**
 * Obtiene todos los locales para mostrar en un selector
 */
export function getLocaleOptions(): Array<{
  value: string;
  label: string;
  flag: string;
}> {
  return SUPPORTED_LOCALES.map((locale) => ({
    value: locale.code,
    label: locale.nativeName,
    flag: locale.flag,
  }));
}

// ============================================================================
// NAMESPACES
// ============================================================================

/**
 * Namespaces de traducción del sistema
 * Cada namespace corresponde a un archivo JSON de traducciones
 */
export const I18N_NAMESPACES = {
  /** Textos comunes (botones, errores, confirmaciones) */
  common: "common",
  /** Navegación y menús */
  navigation: "navigation",
  /** Validaciones y errores de formulario */
  validation: "validation",
  /** Dominio: Sistema */
  system: "system",
  /** Dominio: Seguridad */
  security: "security",
  /** Dominio: Clientes */
  clients: "clients",
  /** Dominio: Reportes */
  reports: "reports",
  /** Dominio: Suscripciones */
  subscriptions: "subscriptions",
  /** Dominio: Notificaciones */
  notifications: "notifications",
  /** Dominio: Documentos */
  documents: "documents",
  /** Dominio: Logística */
  logistics: "logistics",
  /** Dominio: UI */
  ui: "ui",
} as const;

export type I18nNamespace =
  (typeof I18N_NAMESPACES)[keyof typeof I18N_NAMESPACES];

/**
 * Mapeo de domain ID a namespace
 */
export const DOMAIN_TO_NAMESPACE: Record<string, I18nNamespace> = {
  system: I18N_NAMESPACES.system,
  security: I18N_NAMESPACES.security,
  clients: I18N_NAMESPACES.clients,
  reports: I18N_NAMESPACES.reports,
  subscriptions: I18N_NAMESPACES.subscriptions,
  notifications: I18N_NAMESPACES.notifications,
  documents: I18N_NAMESPACES.documents,
  logistics: I18N_NAMESPACES.logistics,
  ui: I18N_NAMESPACES.ui,
};

// ============================================================================
// I18N KEY BUILDERS
// ============================================================================

/**
 * Construye una key de traducción para un módulo
 */
export function buildModuleKey(
  domainNamespace: string,
  moduleId: string,
  key: string,
): string {
  return `${domainNamespace}.${moduleId}.${key}`;
}

/**
 * Construye una key de traducción para un campo
 */
export function buildFieldKey(
  domainNamespace: string,
  moduleId: string,
  field: string,
): string {
  return `${domainNamespace}.${moduleId}.fields.${field}`;
}

/**
 * Construye una key de traducción para un valor de enum
 */
export function buildEnumKey(
  domainNamespace: string,
  moduleId: string,
  field: string,
  value: string,
): string {
  return `${domainNamespace}.${moduleId}.${field}.${value}`;
}

/**
 * Construye una key de traducción para una acción
 */
export function buildActionKey(
  domainNamespace: string,
  moduleId: string,
  action: string,
): string {
  return `${domainNamespace}.${moduleId}.actions.${action}`;
}

/**
 * Construye una key de traducción para un mensaje
 */
export function buildMessageKey(
  domainNamespace: string,
  moduleId: string,
  message: string,
): string {
  return `${domainNamespace}.${moduleId}.messages.${message}`;
}
